import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Space, 
  Popconfirm, 
  message, 
  Row, 
  Col, 
  Flex,
  Typography,
  Tag,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UserOutlined,
  MailOutlined,
  LockOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getOwners, createOwner, updateOwner, deleteOwner } from '../../store/slices/ownerSlice';

const { Title } = Typography;
const { Option } = Select;

const Owners = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.owner);
  
  // Local state for data management
  const [owners, setOwners] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [form] = Form.useForm();

  // Role options
  const roleOptions = [
    { value: 'admin', label: 'Admin', permissions: ['Admin'] },
    { value: 'trainer', label: 'Eğitmen', permissions: ['Trainer'] }
  ];

  // Fetch owners data

  const fetchOwners = async () => {
    try {
      const result = await dispatch(getOwners()).unwrap();
      if (result.items) {
        setOwners(Array.isArray(result.items) ? result.items : []);
      } else {
        setOwners([]);
      }
    } catch (error) {
      message.error('Kullanıcı listesi yüklenirken hata oluştu');
      setOwners([]);
    }
  };

  useEffect(() => {
    fetchOwners();
  }, []);

  // Handle form submission
  const handleSubmit = async (values) => {
    // Normalize username: lowercase and remove non-alphanumeric characters
    const normalizedUsername = (values.username || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    try {
      const selectedRole = roleOptions.find(role => role.value === values.role);
      const ownerData = {
        username: normalizedUsername,
        email: values.email,
        password: values.password,
        permissions: selectedRole.permissions
      };

      if (editingOwner) {
        // Update existing owner
        const result = await dispatch(updateOwner({ ...ownerData, id: editingOwner.id })).unwrap();
        if (result.id) {
          fetchOwners();
          message.success('Kullanıcı başarıyla güncellendi');
        }
      } else {
        // Create new owner
        const result = await dispatch(createOwner(ownerData)).unwrap();
        if (result.id) {
          fetchOwners();
          message.success('Kullanıcı başarıyla oluşturuldu');
        }
      }
      
      handleCloseModal();
    } catch (error) {
      message.error('İşlem sırasında hata oluştu');
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      const result = await dispatch(deleteOwner(id)).unwrap();
      if (result.id) {
        fetchOwners();
        message.success('Kullanıcı başarıyla silindi');
      }
    } catch (error) {
      message.error('Silme işlemi sırasında hata oluştu');
    }
  };

  // Handle edit
  const handleEdit = (owner) => {
    setEditingOwner(owner);
    form.setFieldsValue({
      username: owner.username,
      email: owner.email,
      password: '', // Don't pre-fill password
      role: ''
    });
    setIsModalVisible(true);
  };

  // Handle add new
  const handleAdd = () => {
    setEditingOwner(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalVisible(false);
    setEditingOwner(null);
    form.resetFields();
  };

  // Table columns
  const columns = [
    {
      title: 'Kullanıcı Adı',
      dataIndex: 'username',
      key: 'username',
      render: (text) => (
        <Flex align="center" gap={8}>
          <UserOutlined />
          {text}
        </Flex>
      ),
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      render: (text) => (
        <Flex align="center" gap={8}>
          <MailOutlined />
          {text}
        </Flex>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Düzenle">
            <Button 
              type="primary" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Bu sahibi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Tooltip title="Sil">
              <Button 
                type="primary" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            size="large"
          >
            Yeni Kullanıcı Ekle
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={Array.isArray(owners) ? owners : []}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} / ${total} sahip`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingOwner ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Kullanıcı Adı"
                name="username"
                rules={[
                  { required: true, message: 'Kullanıcı adı gerekli!' },
              { min: 3, message: 'Kullanıcı adı en az 3 karakter olmalı!' },
              {
                pattern: /^[a-z0-9]+$/,
                message: 'Sadece küçük harf ve rakam kullanılabilir (boşluk ve özel karakter yok)'
              }
                ]}
              >
            <Input 
                  prefix={<UserOutlined />} 
              placeholder="Kullanıcı adı girin"
              onChange={(e) => {
                const value = (e.target.value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                form.setFieldsValue({ username: value });
              }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="E-posta"
                name="email"
                rules={[
                  { required: true, message: 'E-posta gerekli!' },
                  { type: 'email', message: 'Geçerli bir e-posta adresi girin!' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="E-posta adresi girin"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Şifre"
                name="password"
                rules={[
                  { required: !editingOwner, message: 'Şifre gerekli!' },
                  { min: 6, message: 'Şifre en az 6 karakter olmalı!' }
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder={editingOwner ? "Değiştirmek için yeni şifre girin" : "Şifre girin"}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Rol"
                name="role"
                rules={[{ required: true, message: 'Rol seçimi gerekli!' }]}
              >
                <Select placeholder="Rol seçin">
                  {roleOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Flex justify="flex-end" gap={8}>
              <Button onClick={handleCloseModal}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingOwner ? 'Güncelle' : 'Ekle'}
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Owners;